import Config from '../config'
import { LIB_IFRAME_ID, getLibIframe, SDK_ERRORS } from './parentConfig'
import {
    createDOMelements,
    locationObserver,
    libIframeMessageReceiver,
    onDragGbtn,
    validateUserProps,
    toggleLibrary,
} from './utils'
import {
    captureSentryError,
    captureSentryEvent,
    initSentry,
} from '../services/sentry'

type searchQueryType = {
    query: string // The text to search - Mandatory
    tags?: string[] // This parameter filters the query by the specified tags - Optional
    length?: number // This parameter controls the number of hits to retrieve - Optional (default 5)
    page?: number // This parameter specifies the page to retrieve - Optional (default 1)
}

const isColorLight = (color: string) => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000

    return brightness > 155
}

// Global vars for SDK.
// Note that func params should be considered as "any", since client can pass any data inside.
declare global {
    interface Window {
        guidde: {
            _i: any
            _apiKey: string
            _tenant: string
            _session: string
            u: string // Host URL
            init: (sdkParams: any) => void
            open: () => void
            close: () => void
            play: (playbookId: any, position: any) => void
            search: (value: any) => void
        }
    }
}

;(function () {
    // Sentry entrypoint for SDK parent
    initSentry(false)

    try {
        if (!window.guidde || !window.guidde._apiKey || !window.guidde.u) {
            captureSentryEvent(SDK_ERRORS.WRONG_SNIPPET, true, {
                snippet: window.guidde,
            })
            return
        }

        const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            )

        if (isMobile) return

        window.guidde.play = () => {
            captureSentryEvent(SDK_ERRORS.CHUNK_LOADING, true)
        }

        const apiKey = window.guidde._apiKey
        const HOST = window.guidde.u
        const HOST_LOCAL = `https://${apiKey}.sdk.${Config.firebase.authDomain}/`
        const CONFIG = {
            api: HOST === 'http://localhost:5000/' ? HOST_LOCAL : HOST,
            iframeSrc:
                HOST === 'http://localhost:5000/'
                    ? 'http://localhost:3000/'
                    : HOST,
        }

        const existingIframe = getLibIframe()
        if (existingIframe) return

        const { container, iframe, controlBtn, overlay } = createDOMelements()
        onDragGbtn({ iframe, container, controlBtn, dragHandle: controlBtn })

        iframe.id = LIB_IFRAME_ID
        iframe.src = CONFIG.iframeSrc
        iframe.allow = `clipboard-read;clipboard-write`

        controlBtn.style.display = 'none'

        const html = document.querySelector('html')
        if (html) {
            html.appendChild(container)
            html.appendChild(overlay)
        }

        iframe.onload = () => {
            fetch(`${CONFIG.api}i/v1/getTenant`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },

                body: JSON.stringify({ apiKey }),
            })
                .then(res => {
                    if (!res.ok) {
                        throw new Error('Request failed')
                    }

                    return res.json()
                })
                .then(response => {
                    if (response?.tenant && response?.session) {
                        const { tenant, session, styles, organizationId } =
                            response

                        window.guidde._tenant = tenant
                        window.guidde._session = session

                        window.guidde.open = () => {
                            toggleLibrary(container, iframe, true)
                        }

                        window.guidde.close = () => {
                            toggleLibrary(container, iframe, false)
                        }

                        // External SDK API functions
                        window.guidde.search = async (
                            params: searchQueryType
                        ): Promise<any> => {
                            if (!params?.query || params?.query?.length < 3) {
                                return new Promise(resolve => {
                                    return resolve({
                                        error: 'Query string must contain at least 3 characters',
                                    })
                                })
                            }
                            return await new Promise(resolve => {
                                // post a message to app.tsx
                                iframe.contentWindow?.postMessage(
                                    {
                                        type: 'SEARCH_FROM_PARENT_SDK',
                                        value: params,
                                    },
                                    CONFIG.iframeSrc
                                )

                                // listen to the response message and return to client
                                window.addEventListener('message', e => {
                                    if (
                                        e.data.action !==
                                        'RETURN_SEARCH_RESPONSE'
                                    )
                                        return
                                    return resolve(e.data.params.data)
                                })
                            })
                        }

                        window.guidde.init = rawUserInfo => {
                            // Start services that waited for iframe
                            libIframeMessageReceiver({
                                iframe,
                                overlay,
                                controlBtn,
                                container,
                            })
                            locationObserver(iframe)

                            const { userInfo, configProps } =
                                validateUserProps(rawUserInfo)

                            iframe.contentWindow?.postMessage(
                                {
                                    type: 'INIT_SDK',
                                    tenant,
                                    session,
                                    userInfo,
                                    apiKey,
                                    styles,
                                    organizationId,
                                    embed: Boolean(configProps.embed),
                                },
                                CONFIG.iframeSrc
                            )

                            const {
                                icon,
                                iconSize,
                                position,
                                color,
                                buttonType,
                                buttonText,
                            } = styles

                            switch (buttonType) {
                                case 'none': {
                                    break
                                }
                                case 'label': {
                                    const textColor = isColorLight(color)
                                        ? 'black'
                                        : 'white'

                                    const wrapper =
                                        document.createElement('div')
                                    const text = document.createElement('div')
                                    const svg = `
                                  <svg width="18" height="22" style="transform: rotate(-90deg)" viewBox="0 0 18 22" fill="${textColor}" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 20L18 2C18 0.89 17.11 -3.89031e-08 16 -8.74228e-08L4 -6.11959e-07C3.46957 -6.35145e-07 2.96086 0.210713 2.58579 0.585786C2.21071 0.960858 2 1.46957 2 2L2 7L-3.0598e-07 7L-6.55671e-07 15L2 15L2 20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22L16 22C17.11 22 18 21.1 18 20ZM4 20L4 2L16 2L16 20L4 20ZM10 15L6 8L14 8" fill="${textColor}"/>
                                  </svg>`

                                    wrapper.style.transform = 'rotate(90deg)'
                                    wrapper.style.display = 'flex'
                                    wrapper.style.flexDirection = 'row'
                                    wrapper.style.alignItems = 'center'
                                    wrapper.style.whiteSpace = 'nowrap'
                                    wrapper.style.fontSize = '13px !important'

                                    text.style.marginLeft = '10px'
                                    text.style.textTransform = 'uppercase'
                                    text.style.color = textColor
                                    text.style.fontSize = '13px !important'

                                    controlBtn.style.padding = '0px'
                                    controlBtn.style.boxSizing = 'content-box'
                                    controlBtn.style.minHeight = '100px'
                                    controlBtn.style.backgroundColor = color

                                    wrapper.innerHTML = svg
                                    text.append(buttonText)

                                    wrapper.append(text)

                                    controlBtn.innerHTML = wrapper.outerHTML
                                    break
                                }
                                case 'icon': {
                                    const defaultSrc = `${CONFIG.iframeSrc}default-logo.svg`
                                    const imgSrc = icon || defaultSrc

                                    controlBtn.innerHTML = `<img alt="Guidde" draggable="false" src="${imgSrc}" height="100%"/>`

                                    controlBtn.style.borderRadius =
                                        '12px 0 0 12px'

                                    if (iconSize) {
                                        controlBtn.style.width = `${iconSize}px`
                                        controlBtn.style.height = `${iconSize}px`
                                        controlBtn.style.left = `-${iconSize}px`
                                    }
                                    break
                                }
                                default: {
                                    captureSentryError(
                                        new Error('Wrong SDK button type.'),
                                        { buttonType }
                                    )
                                }
                            }

                            if (position?.bottom) {
                                controlBtn.style.bottom = position.bottom
                            }

                            controlBtn.style.display = 'flex'

                            if (configProps.embed) {
                                window.guidde.open()
                                container.style.transition = 'none'
                                controlBtn.style.display = 'none'

                                container.style.width = '100vw !important'
                            }

                            const width = configProps.embed
                                ? '100vw'
                                : 'max(650px, 35vw)'
                            // Media query for 1280px break point
                            const changeIframeStyle = e => {
                                container.style.width = width

                                if (configProps.embed) return

                                if (e.matches) {
                                    container.style.width = width
                                } else {
                                    container.style.width = '85vw'
                                }
                            }

                            const media = window.matchMedia(
                                '(min-width: 1280px)'
                            )
                            changeIframeStyle(media)
                            media.addListener(changeIframeStyle)
                        }

                        // It means init function has already been called by the host, perform auto-init
                        if (window.guidde._i) {
                            window.guidde.init(window.guidde._i)
                        }

                        window.guidde.play = (
                            playbookId,
                            position = 'bottomRight'
                        ) => {
                            if (!playbookId || typeof playbookId !== 'string') {
                                captureSentryEvent(
                                    SDK_ERRORS.EMPTY_PB_ID,
                                    true,
                                    {
                                        playbookId,
                                        position,
                                    }
                                )

                                return
                            }

                            iframe.contentWindow?.postMessage(
                                {
                                    type: 'OPEN_PLAYBOOK',
                                    playbookId: String(playbookId),
                                    position,
                                },
                                CONFIG.iframeSrc
                            )
                        }
                    }
                })
                .catch(() => {
                    captureSentryEvent(SDK_ERRORS.INVALID_TENANT, true, {
                        apiKey,
                    })
                })
        }
    } catch (e) {
        captureSentryError(e)
    }
})()
